import { SessionStatus } from "@simbelapp/order-sdk";
import { getToolName, isToolUIPart } from "ai";
import {
  CopilotDataPartEnum,
  CopilotResourceKindEnum,
  CopilotRoleEnum,
  CopilotToolEnum,
  CopilotToolStateEnum,
} from "~/utils/enums/copilot/chat.enums";
import { BookingStatusEnum } from "~/utils/enums/bookings-events.enums";
import { RequestStatus } from "~/utils/enums/request.enums";
import { TrainingPlanStatusEnum } from "~/utils/enums/training-plan-enums";
import { TrainingStatus } from "~/utils/enums/training.enums";
import { copilotResourceCards } from "~/utils/helpers/copilot/resources";
import type { ICopilotUIMessage } from "~/utils/interfaces/copilot-interfaces";

type StatusLabels = Readonly<Record<string, string>>;

const SESSION_STATUS_LABELS: Record<SessionStatus, string> = {
  [SessionStatus.PlanningInProgress]: "Planification en cours",
  [SessionStatus.Summoned]: "Convoquée",
  [SessionStatus.SessionInProgress]: "Formation en cours",
  [SessionStatus.SessionBookingsToEnd]: "Non-déclarée terminée",
  [SessionStatus.SessionBookingsEnded]: "Déclarée terminée",
};

const BOOKING_STATUS_LABELS: Record<BookingStatusEnum, string> = {
  [BookingStatusEnum.BOOKING_TO_BE_CONFIRMED]: "À confirmer",
  [BookingStatusEnum.BOOKING_CONFIRMED]: "Inscrit",
  [BookingStatusEnum.BOOKING_TRAINING_STARTED]: "Formation démarrée",
  [BookingStatusEnum.BOOKING_TRAINING_ENDED]: "Formation terminée",
  [BookingStatusEnum.BOOKING_ON_HOLD]: "En attente",
  [BookingStatusEnum.BOOKING_CANCELED]: "Annulé",
  [BookingStatusEnum.BOOKING_REFUSED]: "Refusé",
  [BookingStatusEnum.BOOKING_MISSING_PROOFS]: "Justificatifs manquants",
  [BookingStatusEnum.BOOKING_BILLABLE]: "Facturable",
  [BookingStatusEnum.BOOKING_INCOMPLETE]: "Incomplet",
  [BookingStatusEnum.BOOKING_NO_SHOW]: "Absent",
  [BookingStatusEnum.BOOKING_REMOVED]: "Supprimé",
};

const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  [RequestStatus.WAITING_MANAGER_INPUT]: "Indications du manager en attente",
  [RequestStatus.WAITING_QUALIFICATION]: "Validation RH en attente",
  [RequestStatus.WAITING_HR_VALIDATION]: "Validation RH en attente",
  [RequestStatus.WAITING_PRIORITY_CONFIRMATION]: "Confirmation priorité en attente",
  [RequestStatus.REQUEST_REFUSED]: "Demande refusée",
  [RequestStatus.PROPOSAL_PENDING]: "Proposition en attente",
  [RequestStatus.PROPOSAL_ACCEPTED]: "Proposition acceptée",
  [RequestStatus.PROPOSAL_CANCELED]: "Proposition annulée",
  [RequestStatus.PROPOSAL_REFUSED]: "Proposition refusée",
  [RequestStatus.NO_VALIDATION_NEEDED]: "Aucune validation nécessaire",
  [RequestStatus.WAITING_FINANCING_VALIDATION]: "En attente de financement",
  [RequestStatus.REQUEST_CANCELED]: "Annulée",
  [RequestStatus.VALIDATED]: "Validée",
  [RequestStatus.ON_HOLD]: "En pause",
  [RequestStatus.PLANNED]: "En cours de traitement",
};

const TRAINING_PLAN_STATUS_LABELS: Record<TrainingPlanStatusEnum, string> = {
  [TrainingPlanStatusEnum.PENDING_PROVIDER]: "En cours de traitement",
  [TrainingPlanStatusEnum.PENDING_EMPLOYEE]: "Choix de date en cours",
  [TrainingPlanStatusEnum.PENDING_INVITATION]: "En attente invitation",
  [TrainingPlanStatusEnum.REGISTERED]: "Inscrit",
  [TrainingPlanStatusEnum.IN_PROGRESS]: "En cours",
  [TrainingPlanStatusEnum.COMPLETED]: "Terminé",
  [TrainingPlanStatusEnum.TO_COMPLETE]: "À compléter",
  [TrainingPlanStatusEnum.CANCELED]: "Annulé",
  [TrainingPlanStatusEnum.NOT_PRESENT]: "Absent",
  [TrainingPlanStatusEnum.NOT_COMPLETED]: "Non terminé",
  [TrainingPlanStatusEnum.TO_ORGANIZE]: "À organiser",
  [TrainingPlanStatusEnum.NO_SHOW]: "Absent",
  [TrainingPlanStatusEnum.ORDER_IN_PROGRESS]: "Commande en cours",
  [TrainingPlanStatusEnum.FINISHED]: "Passée",
  [TrainingPlanStatusEnum.PENDING_DATE_CHOICE]: "Choix de date en cours",
  [TrainingPlanStatusEnum.NO_SUITABLE_DATE]: "Aucune date ne convient",
};

const CATALOG_TRAINING_STATUS_LABELS: Record<TrainingStatus, string> = {
  [TrainingStatus.DRAFT]: "Brouillon",
  [TrainingStatus.LIVE]: "Publiée",
};

const STATUS_LABELS_BY_RESOURCE_KIND: Record<CopilotResourceKindEnum, StatusLabels> = {
  [CopilotResourceKindEnum.COLLABORATOR_PROFILE]: {},
  [CopilotResourceKindEnum.TRAINING_PLAN_SESSION]: SESSION_STATUS_LABELS,
  [CopilotResourceKindEnum.EMPLOYEE_TRAINING]: TRAINING_PLAN_STATUS_LABELS,
  [CopilotResourceKindEnum.EMPLOYEE_REQUEST]: REQUEST_STATUS_LABELS,
  [CopilotResourceKindEnum.COMPANY_REGISTRATION]: BOOKING_STATUS_LABELS,
  [CopilotResourceKindEnum.COMPANY_REQUEST]: REQUEST_STATUS_LABELS,
  [CopilotResourceKindEnum.CATALOG_TRAINING]: CATALOG_TRAINING_STATUS_LABELS,
};

const DEFAULT_STATUS_LABELS: StatusLabels = {
  ...CATALOG_TRAINING_STATUS_LABELS,
  ...REQUEST_STATUS_LABELS,
  ...TRAINING_PLAN_STATUS_LABELS,
  ...BOOKING_STATUS_LABELS,
  ...SESSION_STATUS_LABELS,
  [BookingStatusEnum.BOOKING_ON_HOLD]: "En attente",
  [BookingStatusEnum.BOOKING_CANCELED]: "Annulé",
  [BookingStatusEnum.BOOKING_REFUSED]: "Refusé",
  [BookingStatusEnum.BOOKING_CONFIRMED]: "Inscrit",
  [TrainingPlanStatusEnum.IN_PROGRESS]: "En cours",
  [TrainingPlanStatusEnum.COMPLETED]: "Terminé",
  [TrainingPlanStatusEnum.FINISHED]: "Terminé",
  [TrainingPlanStatusEnum.TO_COMPLETE]: "À compléter",
  [TrainingPlanStatusEnum.NO_SHOW]: "Absent",
};

const STATUS_CODES = [...new Set(Object.keys(DEFAULT_STATUS_LABELS))].sort((left, right) => right.length - left.length);
const RESOURCES_PART_TYPE = `data-${CopilotDataPartEnum.RESOURCES}`;
const LONG_COMPARISON_VALUE_MIN_LENGTH = 120;
const TABLE_ROW_REGEX = /<tr\b[^>]*>[\s\S]*?<\/tr>/gi;
const TABLE_CELL_REGEX = /<(td|th)\b[^>]*>([\s\S]*?)<\/\1>/gi;
const HTML_CONTENT_TAG_REGEX = /<[^>]*>/g;
const HTML_ENTITY_REGEX = /&(?:[a-z]+|#\d+|#x[\da-f]+);/gi;

const STATUS_TOKEN_REGEX = new RegExp(
  `(^|[^A-Za-z0-9_=/?:#&%-])(${STATUS_CODES.join("|")})(?=$|[^A-Za-z0-9_=/?:#&%-])`,
  "g",
);

function labelsFor(resourceKinds: readonly CopilotResourceKindEnum[]): StatusLabels {
  const uniqueKinds = [...new Set(resourceKinds)];
  return uniqueKinds.length === 1 ? STATUS_LABELS_BY_RESOURCE_KIND[uniqueKinds[0]!] : DEFAULT_STATUS_LABELS;
}

/** Replaces complete backend status tokens with the application's French source labels. */
export function localizeCopilotStatusText(
  text: string,
  resourceKinds: readonly CopilotResourceKindEnum[] = [],
): string {
  const contextualLabels = labelsFor(resourceKinds);

  return text.replace(STATUS_TOKEN_REGEX, (_, prefix: string, status: string) => {
    return `${prefix}${contextualLabels[status] ?? DEFAULT_STATUS_LABELS[status] ?? status}`;
  });
}

function tagEndOf(html: string, tagStart: number): number {
  let quote: '"' | "'" | null = null;

  for (let index = tagStart + 1; index < html.length; index += 1) {
    const character = html[index];
    if (quote) {
      if (character === quote) quote = null;
      continue;
    }

    if (character === '"' || character === "'") quote = character;
    else if (character === ">") return index + 1;
  }

  return html.length;
}

/** Localizes only HTML text nodes; tags and their attributes remain byte-for-byte unchanged. */
export function localizeCopilotStatusHtml(
  html: string,
  resourceKinds: readonly CopilotResourceKindEnum[] = [],
): string {
  let localized = "";
  let textStart = 0;
  let cursor = 0;

  while (cursor < html.length) {
    if (html[cursor] !== "<") {
      cursor += 1;
      continue;
    }

    localized += localizeCopilotStatusText(html.slice(textStart, cursor), resourceKinds);
    const tagEnd = tagEndOf(html, cursor);
    localized += html.slice(cursor, tagEnd);
    cursor = tagEnd;
    textStart = cursor;
  }

  return localized + localizeCopilotStatusText(html.slice(textStart), resourceKinds);
}

function resourceKindsOf(message: ICopilotUIMessage): CopilotResourceKindEnum[] {
  return message.parts.flatMap((part) => {
    if (part.type !== RESOURCES_PART_TYPE || !("data" in part)) return [];
    return copilotResourceCards(part.data).map((resource) => resource.kind);
  });
}

function hasCompletedTrainingReview(message: ICopilotUIMessage): boolean {
  return message.parts.some(
    (part) =>
      isToolUIPart(part) &&
      getToolName(part) === CopilotToolEnum.REVIEW_TRAINING &&
      part.state === CopilotToolStateEnum.OUTPUT_AVAILABLE,
  );
}

function visibleTextOf(html: string): string {
  return html.replace(HTML_CONTENT_TAG_REGEX, " ").replace(HTML_ENTITY_REGEX, " ").replace(/\s+/g, " ").trim();
}

function collapseLongComparisonRow(row: string): string {
  const cells = [...row.matchAll(TABLE_CELL_REGEX)];
  if (cells.length < 2) return row;

  const valueCells = cells.length === 2 ? cells.slice(1) : cells.slice(1, -1);
  let collapsed = row;

  for (const cell of valueCells.reverse()) {
    const innerHtml = cell[2] ?? "";
    if (visibleTextOf(innerHtml).length < LONG_COMPARISON_VALUE_MIN_LENGTH) continue;

    const fullCell = cell[0];
    const cellStart = cell.index ?? 0;
    const contentStart = fullCell.indexOf(">") + 1;
    const contentEnd = fullCell.lastIndexOf("</");
    const replacement = `${fullCell.slice(0, contentStart)}…${fullCell.slice(contentEnd)}`;
    collapsed = `${collapsed.slice(0, cellStart)}${replacement}${collapsed.slice(cellStart + fullCell.length)}`;
  }

  return collapsed;
}

function collapseLongComparisonValues(html: string): string {
  return html.replace(TABLE_ROW_REGEX, collapseLongComparisonRow);
}

/** Keeps user-authored text verbatim and derives assistant status vocabulary from its resource payload. */
export function localizeCopilotMessageText(message: ICopilotUIMessage, text: string): string {
  if (message.role !== CopilotRoleEnum.ASSISTANT) return text;
  return localizeCopilotStatusText(text, resourceKindsOf(message));
}

/** Applies the same assistant-only rule to sanitized HTML while preserving its tags and attributes. */
export function localizeCopilotMessageHtml(message: ICopilotUIMessage, html: string): string {
  if (message.role !== CopilotRoleEnum.ASSISTANT) return html;

  const localized = localizeCopilotStatusHtml(html, resourceKindsOf(message));
  return hasCompletedTrainingReview(message) ? collapseLongComparisonValues(localized) : localized;
}
